import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { ChevronDown, RefreshCw, Gavel, AlertTriangle, ExternalLink } from "lucide-react";
import { liveAuctions, money } from "@/lib/bidCenterApi";
import { fmtMT, timeMT } from "@/lib/mountainTime";

export const LiveCtx = createContext(null);
const stop = (e) => e.stopPropagation();

// One request for all ongoing horses every 15 s (only while visible); the clock offset makes the countdown server-based.
export function useLiveAuctions(horseIds, enabled) {
  const [s, setS] = useState({ items: {}, offset: 0, at: null, error: false, loading: false });
  const key = horseIds.join(",");
  const ref = useRef(key);
  ref.current = key;
  const refresh = useCallback(() => {
    if (!enabled || !ref.current) return;
    setS((x) => ({ ...x, loading: true }));
    const t0 = Date.now();
    liveAuctions(ref.current.split(","))
      .then((d) => setS({ items: d.items, offset: new Date(d.server_now).getTime() - (t0 + Date.now()) / 2, at: Date.now(), error: false, loading: false }))
      .catch(() => setS((x) => ({ ...x, error: true, loading: false })));
  }, [enabled]);
  useEffect(() => {
    refresh();
    const t = setInterval(() => { if (document.visibilityState === "visible") refresh(); }, 15000);
    return () => clearInterval(t);
  }, [key, refresh]);
  return { ...s, refresh };
}

function useNow(offset) {
  const [now, setNow] = useState(Date.now() + offset);
  useEffect(() => { setNow(Date.now() + offset); const t = setInterval(() => setNow(Date.now() + offset), 1000); return () => clearInterval(t); }, [offset]);
  return now;
}

const mmss = (ms) => {
  const s = Math.max(0, Math.floor(ms / 1000)), h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60), x = String(s % 60).padStart(2, "0");
  if (h >= 24) return `${Math.floor(h / 24)}d ${h % 24}h`;
  return h ? `${h}h ${String(m).padStart(2, "0")}m` : `${String(m).padStart(2, "0")}:${x}`;  // hours/minutes until the final hour, then MM:SS
};

function Countdown({ it, offset, testId }) {
  const now = useNow(offset);
  const left = it.closes_at ? new Date(it.closes_at).getTime() - now : null;
  const ext = it.soft_close?.extensions || 0;
  const final = left != null && left > 0 && left < 60000;
  let label, tone, big;
  if (it.phase === "closed") [label, tone, big] = ["Closed", "bg-stone-800 text-white", "Closed"];
  else if (it.phase === "pending_verification") [label, tone, big] = ["PENDING VERIFICATION", "bg-[#FEF3C7] text-[#92400E] ring-2 ring-[#F59E0B]", "00:00"];
  else if (it.phase === "no_schedule") [label, tone, big] = ["No closing time set", "bg-stone-100 text-stone-600", "--:--"];
  else if (it.phase === "upcoming" && it.starts_at) [label, tone, big] = ["Bidding opens in", "bg-stone-200 text-stone-800", mmss(new Date(it.starts_at).getTime() - now)];
  else if (left != null && left > 0) [label, tone, big] = [it.phase === "upcoming" ? "Bidding not open yet" : left < 600000 ? "LAST 10 MINUTES" : left < 3600000 ? "Final hour" : "Time left",
    left < 600000 ? "bg-red-700 text-white" : ext ? "bg-[#B45309] text-white" : left < 3600000 ? "bg-[#9A3412] text-white ring-2 ring-[#FDBA74]" : "bg-stone-900 text-white", mmss(left)];
  else [label, tone, big] = [it.awaiting_time_count ? "Closing / Waiting for exact Facebook time" : "Closing / Reconciling", "bg-[#FEF3C7] text-[#92400E]", "00:00"];
  return (
    <div data-testid={`${testId}-countdown`} className={`rounded-lg px-2 py-1 text-right ${tone} ${final ? "animate-pulse ring-4 ring-red-400" : ""}`} data-final-minute={final ? "true" : undefined}>
      <div data-testid={`${testId}-countdown-label`} className="text-[10px] font-semibold uppercase tracking-wide leading-tight">{label}</div>
      <div data-testid={`${testId}-countdown-time`} className={`font-mono font-bold tabular-nums leading-tight ${left != null && left > 0 && left < 3600000 ? "text-2xl" : "text-lg"}`}>{big}</div>
      {ext > 0 && it.closes_at && it.phase !== "closed" && <div data-testid={`${testId}-countdown-extended`} className="text-[10px] font-semibold leading-tight">Extended to {timeMT(it.closes_at)}{ext > 1 ? ` · ${ext}×` : ""}</div>}
    </div>
  );
}

const CLS = { valid: ["Valid bid", "bg-emerald-100 text-emerald-800"], review: ["Needs review", "bg-[#FEF3C7] text-[#92400E]"], invalid: ["Invalid bid", "bg-red-100 text-red-800"],
  warning: ["BA Horse Sales", "bg-blue-100 text-blue-800"], comment: ["Comment", "bg-stone-100 text-stone-600"], bid: ["Bid", "bg-stone-100 text-stone-700"] };

function Comment({ c, testId }) {
  const [l, cls] = CLS[c.classification] || CLS.comment;
  return (
    <li data-testid={testId} className={`px-2 py-1.5 border-b last:border-0 ${c.classification === "review" ? "bg-[#FFFBEB] border-l-4 border-l-[#D97706]" : ""}`}>
      <div className="flex items-center gap-1.5 text-[11px]">
        <b className="truncate text-stone-900">{c.author || "Unknown"}</b>
        <span className={`shrink-0 rounded-full px-1.5 font-semibold ${cls}`}>{l}</span>
        {c.edited && <span className="shrink-0 rounded-full px-1.5 bg-violet-100 text-violet-800 font-semibold">Edited</span>}
        {c.not_seen && <span className="shrink-0 rounded-full px-1.5 bg-stone-200 text-stone-700 font-semibold">Not seen (deleted?)</span>}
      </div>
      <p className={`text-xs text-stone-800 break-words ${c.not_seen ? "line-through text-stone-500" : ""}`}>{c.text}</p>
      {c.edited && c.versions.length > 0 && <p className="text-[10px] text-stone-500 break-words">Before: {c.versions.map((v) => `“${v}”`).join(" → ")}</p>}
      {c.classification !== "comment" && c.issues.length > 0 && <p className="text-[10px] text-[#92400E]">{c.issues.join(" · ")}</p>}
      <p className="text-[10px] text-stone-500">{fmtMT(c.at)}{c.time_precision !== "exact" ? " (approx.)" : ""}{c.link && <> · <a href={c.link} target="_blank" rel="noopener noreferrer" className="underline">Facebook</a></>}</p>
    </li>
  );
}

function SyncLine({ it, live, testId }) {
  const h = it.health || {};
  const late = it.phase !== "closed" && ["stale", "outdated"].includes(h.level);  // a settled auction is no longer synced on purpose
  const bad = live.error || it.sync_warning || it.phase === "pending_verification" || late || ["error", "never"].includes(h.level) || h.complete === false;
  const msg = live.error ? "Can't reach the server — showing the last data"
    : it.phase === "pending_verification" ? "Pending verification: Facebook completeness not confirmed — no winner declared"
    : it.sync_warning ? `Facebook sync problem: ${it.sync_warning}`
    : h.level === "never" ? "Facebook not synced yet" : h.level === "error" ? "Last Facebook sync failed"
    : late ? `Facebook monitoring delayed (${h.minutes_ago} min)` : h.complete === false ? "Last Facebook read was incomplete" : null;
  return (
    <div data-testid={`${testId}-sync`} className={`flex items-start gap-1 text-[10px] ${bad ? "text-[#92400E] font-semibold" : "text-stone-500"}`}>
      {bad && <AlertTriangle className="h-3 w-3 shrink-0 mt-0.5" />}
      <span className="min-w-0 break-words">{msg ? `${msg} · ` : ""}FB sync {h.last_synced_at ? fmtMT(h.last_synced_at) : "never"}</span>
      <button type="button" data-testid={`${testId}-refresh`} onClick={(e) => { stop(e); live.refresh(); }} aria-label="Refresh live auction" className="ml-auto h-10 w-10 -my-2 shrink-0 inline-flex items-center justify-center rounded-full hover:bg-stone-100">
        <RefreshCw className={`h-3.5 w-3.5 ${live.loading ? "animate-spin" : ""}`} />
      </button>
    </div>
  );
}

export function LiveAuction({ c, testId, className = "" }) {
  const live = useContext(LiveCtx);
  const [open, setOpen] = useState(false);
  const it = live?.items?.[c.ids.horse_id];
  if (!it) return null;
  const hb = it.high_bid, t = `${testId}-live`;
  return (
    <div data-testid={t} onClick={stop} onKeyDown={stop} className={`mt-2 rounded-lg border border-stone-200 bg-stone-50 p-2 space-y-1.5 cursor-default ${className}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="text-[10px] uppercase tracking-wide text-stone-500 font-semibold flex items-center gap-1"><Gavel className="h-3 w-3" />High bid</div>
          {hb ? <><div data-testid={`${t}-high`} className="text-lg font-bold text-stone-900 leading-tight">{money(hb.amount, hb.currency)}</div>
            <div data-testid={`${t}-bidder`} className="text-[11px] text-stone-600 truncate">{hb.bidder}</div></>
            : <div data-testid={`${t}-high`} className="text-sm font-semibold text-stone-600">No bids yet</div>}
        </div>
        <Countdown it={it} offset={live.offset} testId={t} />
      </div>
      {it.phase === "upcoming" && it.starts_at && <div data-testid={`${t}-opens`} className="text-[10px] text-stone-600">Bidding opens {fmtMT(it.starts_at)}</div>}
      {it.soft_close?.scheduled_close && <div data-testid={`${t}-close`} className="text-[10px] text-stone-600">Closes {fmtMT(it.closes_at)}{it.soft_close.extensions ? ` (scheduled ${fmtMT(it.soft_close.scheduled_close)})` : ""}</div>}
      {it.review_count > 0 && <div data-testid={`${t}-review`} className="text-[11px] font-semibold text-[#92400E]">{it.review_count} bid{it.review_count === 1 ? " needs" : "s need"} review</div>}
      <SyncLine it={it} live={live} testId={t} />
      <button type="button" data-testid={`${t}-toggle`} aria-expanded={open} onClick={(e) => { stop(e); setOpen(!open); }} className="w-full min-h-[36px] flex items-center gap-1 text-xs font-semibold text-stone-800">
        <ChevronDown className={`h-4 w-4 transition-transform ${open ? "rotate-180" : ""}`} />Facebook Comments ({it.comments.length})
      </button>
      {open && <>
        <ul data-testid={`${t}-comments`} className="max-h-[300px] overflow-y-auto overscroll-contain rounded-md border bg-white" style={{ WebkitOverflowScrolling: "touch" }}>
          {it.comments.length ? it.comments.map((x) => <Comment key={x.id} c={x} testId={`${t}-comment-${x.id}`} />) : <li className="p-2 text-xs text-stone-500">No comments synced yet.</li>}
        </ul>
        <a data-testid={`${t}-bid-mgmt`} href={`/bids?horse=${c.ids.horse_id}`} className="inline-flex items-center gap-1 text-[11px] font-semibold text-blue-800 underline"><ExternalLink className="h-3 w-3" />Open in Bid Management</a>
      </>}
    </div>
  );
}
